/**
 * VoiceAssistant Component
 * ========================
 * Female voice announcements using Web Speech API (SpeechSynthesis).
 * Works in Chrome, Edge, Safari, and Firefox.
 * 
 * Features:
 * - Natural female voice for attendance events
 * - Time-of-day aware greetings (Good morning/afternoon/evening)
 * - Login, Logout, Break In/Out announcements
 * - Face verification status announcements
 * - Multilingual support (defaults to English)
 */

// Check if speech synthesis is available
const isSpeechAvailable = () => {
  return typeof window !== 'undefined' && window.speechSynthesis && window.speechSynthesis.speak;
};

// Get a female voice - prefer Google UK English Female, then any female voice
const getFemaleVoice = () => {
  if (!isSpeechAvailable()) return null;
  
  const voices = window.speechSynthesis.getVoices();
  
  // Preference order for female voices
  const preferredVoices = [
    'Google UK English Female',
    'Google US English',
    'Microsoft Zira Desktop - English (United States)',
    'Samantha',
    'Karen',
    'Moira',
    'Tessa',
  ];
  
  // Try preferred voices first
  for (const preferred of preferredVoices) {
    const found = voices.find(v => v.name === preferred);
    if (found) return found;
  }
  
  // Fallback: any female-sounding voice (check by name patterns)
  const femaleKeywords = ['female', 'woman', 'girl', 'samantha', 'zira', 'karen', 'moira', 'tessa', 'victoria', 'helena'];
  for (const voice of voices) {
    const name = voice.name.toLowerCase();
    if (femaleKeywords.some(k => name.includes(k))) {
      return voice;
    }
  }
  
  // Last resort: return first English voice
  const englishVoice = voices.find(v => v.lang.startsWith('en'));
  return englishVoice || voices[0] || null;
};

// Ensure voices are loaded (they load asynchronously)
const ensureVoicesLoaded = () => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      resolve(false);
      return;
    }
    
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      resolve(true);
      return;
    }
    
    // Voices not yet loaded, wait for them
    window.speechSynthesis.onvoiceschanged = () => {
      resolve(true);
    };
    
    // Timeout fallback
    setTimeout(() => resolve(false), 3000);
  });
};

/**
 * Speak text using the browser's speech synthesis with a female voice.
 * @param {string} text - Text to speak
 * @param {Object} options - Options
 * @param {number} options.rate - Speech rate (0.1 to 10, default 1.0)
 * @param {number} options.pitch - Voice pitch (0 to 2, default 1.1 for female)
 * @param {number} options.volume - Volume (0 to 1, default 1.0)
 * @returns {Promise} - Resolves when speech ends or is cancelled
 */
const speak = (text, options = {}) => {
  return new Promise((resolve) => {
    if (!isSpeechAvailable() || !text) {
      // Fallback: console log
      console.log(`[VOICE] ${text}`);
      resolve();
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    
    const voice = getFemaleVoice();
    if (voice) {
      utterance.voice = voice;
    }
    
    utterance.rate = options.rate || 0.9;  // Slightly slower for clarity
    utterance.pitch = options.pitch || 1.2; // Slightly higher pitch for female
    utterance.volume = options.volume || 1.0;
    utterance.lang = options.lang || 'en-US';
    
    utterance.onend = resolve;
    utterance.onerror = resolve;
    
    window.speechSynthesis.speak(utterance);
  });
};

/**
 * Get time-of-day greeting based on current hour.
 */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Good evening';
};

/**
 * Speak a welcome/greeting message appropriate to the time of day.
 */
const speakGreeting = async (employeeName) => {
  const greeting = getGreeting();
  await speak(`${greeting} ${employeeName}. Welcome to work. Have a great day ahead.`);
};

/**
 * Voice announcement functions for attendance events.
 */
export const VoiceAnnouncements = {
  /**
   * Initialize the voice system (must be called after user interaction).
   */
  init: async () => {
    await ensureVoicesLoaded();
    const voice = getFemaleVoice();
    if (voice) {
      console.log(`[VOICE] Using voice: ${voice.name} (${voice.lang})`);
    } else {
      console.log('[VOICE] No female voice found, using default');
    }
    return !!voice;
  },

  /**
   * Speak login success with time-of-day greeting.
   */
  loginSuccess: async (employeeName, loginTime) => {
    const firstName = employeeName.split(' ')[0];
    const greeting = getGreeting();
    const timeStr = loginTime ? new Date(loginTime).toLocaleTimeString('en-US', { 
      hour: 'numeric', minute: '2-digit' 
    }) : '';
    await speak(`${greeting} ${firstName}.`);
    await speak(`Login successful at ${timeStr}. Face verified. Welcome.`);
  },

  /**
   * Speak login success without face verification.
   */
  loginSuccessPending: async (employeeName, loginTime) => {
    const firstName = employeeName.split(' ')[0];
    const greeting = getGreeting();
    const timeStr = loginTime ? new Date(loginTime).toLocaleTimeString('en-US', { 
      hour: 'numeric', minute: '2-digit' 
    }) : '';
    await speak(`${greeting} ${firstName}.`);
    await speak(`Login recorded at ${timeStr}. Face verification is pending.`);
  },

  /**
   * Speak logout message.
   */
  logoutMessage: async (employeeName, workingHours, workingMinutes) => {
    const firstName = employeeName.split(' ')[0];
    await speak(`Goodbye ${firstName}.`);
    let timeStr = '';
    if (workingHours > 0) {
      timeStr = `${workingHours} hours and ${workingMinutes} minutes`;
    } else {
      timeStr = `${workingMinutes} minutes`;
    }
    await speak(`Thank you for your work today. Total working time ${timeStr}. Have a safe journey home.`);
  },

  /**
   * Speak break started message.
   */
  breakIn: async (employeeName, breakNumber) => {
    const firstName = employeeName.split(' ')[0];
    await speak(`Break time ${firstName}.`);
    await speak(`Starting break number ${breakNumber}. Enjoy your break and relax.`);
  },

  /**
   * Speak break ended message.
   */
  breakOut: async (employeeName, durationMinutes) => {
    const firstName = employeeName.split(' ')[0];
    await speak(`Welcome back ${firstName}.`);
    await speak(`Break ended after ${durationMinutes} minutes. Continue your work.`);
  },

  /**
   * Speak face verification success.
   */
  faceVerified: async (employeeName) => {
    await speak(`Face recognized. Welcome ${employeeName}.`);
  },

  /**
   * Speak face detection instructions.
   */
  faceInstruction: async () => {
    await speak('Please look at the camera for face verification.');
  },

  /**
   * Speak face verification failed.
   */
  faceFailed: async () => {
    await speak('Face verification failed. Please try again or contact HR.');
  },

  /**
   * Speak no face detected.
   */
  noFaceDetected: async () => {
    await speak('No face detected. Please ensure your face is clearly visible and look directly at the camera.');
  },

  /**
   * Speak already logged in.
   */
  alreadyLoggedIn: async (employeeName) => {
    await speak(`${employeeName}, you are already logged in today.`);
  },

  /**
   * Speak not logged in.
   */
  notLoggedIn: async () => {
    await speak('You are not logged in. Please login first.');
  },

  /**
   * Speak error message.
   */
  error: async (message) => {
    await speak(`Error: ${message || 'An error occurred. Please try again.'}`);
  },

  /**
   * Speak welcome message when page loads.
   */
  welcome: async (employeeName) => {
    const greeting = getGreeting();
    await speak(`${greeting} ${employeeName}. Welcome to the Attendance Management System.`);
  },

  /**
   * Speak a custom message.
   */
  speak: async (text) => {
    await speak(text);
  },
};

export default VoiceAnnouncements;